/**
 * Why a curated cart was closed lost.
 *
 * The wording is inherited from the enquiry-management-system, which is now
 * decommissioned — there is no live system to stay in sync with, so treat this file
 * as the source of truth and change it freely. It was adopted rather than invented
 * because the taxonomy is a good one: seven of the reasons point inward, at our own
 * execution, which is what stops a lost-reason report from only ever saying "price".
 *
 * Must stay in step with QuoteController.LOST_REASONS, which rejects anything
 * outside the set — that one is a live coupling.
 *
 * The grouping is ours; EMS presents one flat list of sixteen. Sixteen is past the
 * point where people read to the end, and the grouping does more than shorten the
 * scan: it puts the reasons that blame us next to each other under a heading that
 * says so. That split is the whole value of the taxonomy — without it a lost-reason
 * report only ever says "we lost on price" — so it should be visible at the moment
 * someone picks, not just in the report afterwards.
 *
 * Grouping is presentation only. Nothing is persisted but the reason string, so
 * regrouping later needs no migration and no backfill.
 */
export const LOST_REASON_GROUPS = [
    {
        label: 'Client / market',
        options: [
            'Competitor pricing issue',
            'Client chose known vendor',
            'Budget not qualified',
            'Event cancelled',
            'Client went cold / no revival',
        ],
    },
    {
        label: 'Our execution',
        options: [
            'Slow first response',
            'Weak follow-up',
            'No mockups/visuals shared',
            'Poor proposal quality',
            'Internal delay (costing/sampling)',
            'No closure attempt',
            'Confusing communication / no SPOC',
        ],
    },
    {
        label: 'Product / supply',
        options: [
            'Stock availability issue',
            'Alternate product',
            'Timeline',
        ],
    },
    {
        // Not a loss cause — it means the cart should never have counted as a loss.
        // Its own group rather than an entry under Other, so these can be excluded
        // from loss analysis at a glance: a large share of historical closed-lost
        // carts turned out to be housekeeping sweeps of test and junk records, and
        // folding those in with real losses is what made the total unreadable.
        label: 'Housekeeping',
        options: ['Duplicate or test cart'],
    },
    {
        label: 'Other',
        options: ['Other'],
    },
].map(g => ({ label: g.label, options: g.options.map(r => ({ value: r, label: r })) }));

/** Flat list of every valid reason, for validation and iteration. */
export const LOST_REASONS = LOST_REASON_GROUPS.flatMap(g => g.options);

/** Reason requiring a written explanation — 'Other' says nothing on its own. */
export const LOST_REASON_REQUIRING_NOTE = 'Other';

const GROUP_OF = LOST_REASON_GROUPS.reduce((acc, g) => {
    g.options.forEach(o => { acc[o.value] = g.label; });
    return acc;
}, {});

/**
 * Which group a stored reason belongs to. Reasons closed before a regrouping —
 * or retired from the list entirely — fall back to 'Other' rather than creating
 * a phantom group in the breakdown.
 */
export const lostReasonGroup = (value) => GROUP_OF[value] || 'Other';

/**
 * Display label for a stored reason. An identity function today; it exists so a
 * reason retired from the list still renders on historical carts, and so call
 * sites don't need changing if these ever become codes.
 */
export const lostReasonLabel = (value) => value || '';
